import { formatNumber, percent } from './format'

/** Nis2_board::WINDOW_DAYS - aceeasi fereastra ca pe tabloul ITIL. */
export const NIS2_WINDOW_DAYS = 30

export type LetterState = 'measured' | 'manual' | 'off'

export interface Evidence {
  label: string
  value: string
  bad?: boolean
}

export interface Letter {
  key: string
  name: string
  asks: string
  state: LetterState
  score: number | null
  link: string | null
  link_label: string | null
  evidence: Evidence[]
  /** De ce litera e 'off', cand nu e "modulul nu e pornit" - ex. nicio sursa de backup. */
  off_reason?: string
}

function rec(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : {}
}

function n(v: unknown): number {
  return Number(v) || 0
}

/**
 * Literele (a)-(j) din nis2/index.php, din aceleasi chei pe care le da
 * GET /api/v2/overview/nis2.
 *
 * ADRESELE sunt scrise AICI, nu vin de la server: `Nis2Board::page()` da
 * numai cifrele. Randurile de `link` au fost transcrise ca atare din macheta
 * veche, unde stateau in `base_url(...)`, si trei dintre ele erau adrese de
 * portal fara ruta in v4:
 *
 *   `policies`         -> ecranul politicilor e `easeedesk/it_policies` (acolo
 *                        sta textul NIS2 al fiecarei politici);
 *   `opteemum/devices` -> nu e nici macar in `OpteemumCut::PAGES`, deci in
 *                        portal raspundea 404; intrarea in modul e tabloul lui;
 *   `inventoree`       -> tot 404 in portal (`Inventoree` n-are `index`), iar
 *                        `_remap` scrie negru pe alb ca intrarea e tabloul.
 *
 * `hrefOf` pune `/app` numai peste adresele pe care le stie (`IN_APP`), si
 * lasa restul scrise ca in portal - deliberat, ca sa se vada CE lipseste. Deci
 * o adresa fara ruta iesea de aici `/policies`, fara `/app`, adica afara din
 * aplicatie. Leacul e adresa corecta, nu o exceptie in helper.
 */
export function nis2Letters(page: Record<string, unknown>): Letter[] {
  const days = n(page.days) || NIS2_WINDOW_DAYS
  const fleet = rec(page.fleet)
  const patching = rec(page.patching)
  const protection = rec(page.protection)
  const encryption = rec(page.encryption)
  const access = rec(page.access)
  const backup = rec(page.backup)
  const policies = rec(page.policies)
  const incidents = rec(page.incidents)
  const changes = rec(page.changes)

  const letters: Letter[] = []

  letters.push({
    key: 'a',
    name: 'Risk analysis and security policies',
    asks: 'written policies, kept current, for every area of risk',
    state: policies.ready ? 'measured' : 'off',
    score: policies.ready ? percent(n(policies.filled), n(policies.templates)) : null,
    link: 'easeedesk/it_policies',
    link_label: 'Policies',
    evidence: policies.ready
      ? [
          { label: 'NIS2 policies filled in', value: `${formatNumber(n(policies.filled))} of ${formatNumber(n(policies.templates))}` },
          { label: 'reviewed in the last 12 months', value: formatNumber(n(policies.reviewed)) },
        ]
      : [],
  })

  letters.push({
    key: 'b',
    name: 'Incident handling',
    asks: 'incidents get logged, handled in time, and leave a trail',
    state: incidents.ready ? 'measured' : 'off',
    score: incidents.ready ? (n(incidents.open) > 0 ? percent(n(incidents.open) - n(incidents.breached), n(incidents.open)) : 100) : null,
    link: 'easeedesk/tickets',
    link_label: 'Tickets',
    evidence: incidents.ready
      ? [
          { label: 'open incidents', value: formatNumber(n(incidents.open)) },
          { label: 'past SLA, of those open', value: formatNumber(n(incidents.breached)), bad: n(incidents.breached) > 0 },
          { label: `resolved in ${days} days`, value: formatNumber(n(incidents.resolved)) },
          {
            label: 'average time to resolve',
            value: incidents.mttr_hours === null || incidents.mttr_hours === undefined ? '—' : `${incidents.mttr_hours}h`,
          },
        ]
      : [],
  })

  /*
   * (c) citea numai Protectee - un modul ascuns din meniu - si ducea acolo.
   * Acum serverul numara si joburile gasite de agent (Windows Server Backup,
   * Veeam, SQL Server, Time Machine, restic, borg), iar cand nicio masina nu
   * raporteaza vreun backup litera spune ca NU SE POATE MASURA, nu 0%.
   */
  const backupSources = Array.isArray(backup.sources) ? (backup.sources as unknown[]).map(String) : []
  const backupMeasured = Boolean(backup.ready) && n(backup.configured) > 0
  const backupFrom = [
    backupSources.includes('agent') ? 'backup jobs the agent finds (Windows Server Backup, Veeam, SQL Server, Time Machine, restic, borg)' : '',
    backupSources.includes('protectee') ? 'Protectee' : '',
  ]
    .filter(Boolean)
    .join(' and ')
  letters.push({
    key: 'c',
    name: 'Business continuity and backups',
    asks: 'a backup that actually runs, not just one configured once',
    state: backupMeasured ? 'measured' : 'off',
    score: backupMeasured ? percent(n(backup.ok_devices), n(backup.configured)) : null,
    link: null,
    link_label: null,
    evidence: backupMeasured
      ? [
          { label: 'machines with a backup configured', value: formatNumber(n(backup.configured)) },
          { label: `with a successful copy in ${NIS2_WINDOW_DAYS} days`, value: formatNumber(n(backup.ok_devices)) },
          { label: 'failed copies in the window', value: formatNumber(n(backup.errors)), bad: n(backup.errors) > 0 },
          ...(backupFrom ? [{ label: 'read from', value: backupFrom }] : []),
        ]
      : [],
    off_reason: backup.ready
      ? 'cannot be measured: no machine reports a backup job yet - neither the agent nor Protectee has seen one'
      : 'cannot be measured: this instance has neither the agent backup jobs nor Protectee',
  })

  letters.push({
    key: 'd',
    name: 'Supply chain security',
    asks: 'supplier relationships, weighed up and written down',
    state: 'manual',
    score: null,
    link: 'easeedesk/it_policies',
    link_label: 'Policies',
    evidence: [{ label: 'the evidence is a written policy, not telemetry', value: 'to document' }],
  })

  const patchClean = patching.ready ? Math.max(0, n(fleet.total) - n(patching.missing)) : null
  letters.push({
    key: 'e',
    name: 'Acquisition, development and vulnerability handling',
    asks: 'patches get installed, and a missing one is visible',
    state: patching.ready ? 'measured' : 'off',
    score: patching.ready ? percent(patchClean ?? 0, n(fleet.total)) : null,
    link: 'opteemum/dashboard',
    link_label: 'OS Updates',
    evidence: patching.ready
      ? [
          { label: 'machines fully patched', value: `${formatNumber(patchClean ?? 0)} of ${formatNumber(n(fleet.total))}` },
          { label: 'machines missing patches', value: formatNumber(n(patching.missing)), bad: n(patching.missing) > 0 },
        ]
      : [],
  })

  letters.push({
    key: 'f',
    name: 'Measuring how well the measures work',
    asks: 'not "we have a process", but "the process is followed" - and the number that shows it',
    state: changes.ready ? 'measured' : 'off',
    score: changes.ready ? (changes.control_rate === null || changes.control_rate === undefined ? null : n(changes.control_rate)) : null,
    link: 'adm/changes',
    link_label: 'Changes',
    evidence: changes.ready
      ? [
          {
            label: 'change control rate',
            value: changes.control_rate === null || changes.control_rate === undefined ? '—' : `${changes.control_rate}%`,
          },
          {
            label: `unauthorised changes in ${days} days`,
            value: formatNumber(n(changes.unauthorized)),
            bad: n(changes.unauthorized) > 0,
          },
        ]
      : [],
  })

  const macScope = n(protection.mac_scope)
  const avScope = n(protection.av_scope)
  const fwScope = n(protection.fw_scope)
  const hygiene: Evidence[] = protection.ready
    ? [
        { label: 'antivirus on', value: `${formatNumber(n(protection.av_on))} of ${formatNumber(avScope)} that report it` },
        { label: 'firewall on', value: `${formatNumber(n(protection.fw_on))} of ${formatNumber(fwScope)} that report it` },
      ]
    : []
  const quiet = Math.max(0, n(protection.reporting) - avScope - macScope)
  if (quiet > 0) hygiene.push({ label: 'have not reported their guard yet', value: `${formatNumber(quiet)} machines` })
  if (macScope > 0) {
    hygiene.push({ label: 'Linux, guarded by SELinux / AppArmor', value: `${formatNumber(macScope)} machines - status not collected yet` })
  }
  if (access.ready && n(access.reporting) > 0) {
    hygiene.push({
      label: 'failed logins in the last 24 hours',
      value:
        `${formatNumber(n(access.failed_24h))} on ${formatNumber(n(access.reporting))} machines` +
        (n(access.under_attack) > 0 ? ` - ${formatNumber(n(access.under_attack))} under brute-force right now (20+ in an hour)` : '') +
        (n(access.lockouts_24h) > 0 ? `, ${formatNumber(n(access.lockouts_24h))} account lockouts` : ''),
    })
    hygiene.push({
      label: 'local administrators',
      value:
        `${formatNumber(n(access.admins))} accounts across the fleet` +
        (n(access.many_admins) > 0 ? ` - ${formatNumber(n(access.many_admins))} machines with 6 or more` : '') +
        (n(access.new_admins_7d) > 0 ? `, ${formatNumber(n(access.new_admins_7d))} became administrator in the last 7 days` : ''),
    })
  } else if (access.ready) {
    hygiene.push({
      label: 'failed logins and local administrators',
      value: 'not reported yet - agents send them with the next status report',
    })
  }

  letters.push({
    key: 'g',
    name: 'Basic cyber hygiene',
    asks: 'the guard each system expects, and a firewall on every machine',
    state: protection.ready ? 'measured' : 'off',
    score: protection.ready ? percent(n(protection.av_on), avScope) : null,
    link: 'opteemum/dashboard',
    link_label: 'Opteemum',
    evidence: hygiene,
  })

  const encMeasured = Boolean(encryption.ready) && n(encryption.reporting) > 0
  const encEvidence: Evidence[] = []
  if (encMeasured) {
    encEvidence.push({
      label: 'machines with every data volume encrypted and protected',
      value: `${formatNumber(n(encryption.protected_devices))} of ${formatNumber(n(encryption.reporting))} that report it`,
    })
    if (n(encryption.open_volumes) > 0) {
      encEvidence.push({
        label: 'volumes in the clear (no encryption, or protection suspended)',
        value: `${formatNumber(n(encryption.open_volumes))} on ${formatNumber(n(encryption.open_devices))} machines`,
      })
    }
  } else if (encryption.ready) {
    encEvidence.push({
      label: 'disk encryption',
      value: 'not reported yet - agents send BitLocker / FileVault / LUKS with the next status report',
    })
  }
  encEvidence.push({ label: 'the written rule on what gets encrypted, and how', value: 'to document' })

  letters.push({
    key: 'h',
    name: 'Cryptography and encryption',
    asks: 'every data volume encrypted, and written rules on what gets encrypted, and how',
    state: encMeasured ? 'measured' : 'manual',
    score: encMeasured ? percent(n(encryption.protected_devices), n(encryption.reporting)) : null,
    link: encMeasured ? 'opteemum/dashboard' : 'easeedesk/it_policies',
    link_label: encMeasured ? 'Opteemum' : 'Policies',
    evidence: encEvidence,
  })

  letters.push({
    key: 'i',
    name: 'People, access control and assets',
    asks: 'every machine has an owner and sits in the inventory',
    state: 'measured',
    score: percent(n(fleet.owned), n(fleet.total)),
    link: 'inventoree/dashboard',
    link_label: 'Inventoree',
    evidence: [
      { label: 'machines with an owner', value: `${formatNumber(n(fleet.owned))} of ${formatNumber(n(fleet.total))}` },
      { label: 'machines with a CMDB item', value: formatNumber(n(fleet.in_cmdb)) },
      { label: `seen in the last ${NIS2_WINDOW_DAYS} days`, value: formatNumber(n(fleet.seen)) },
    ],
  })

  /*
   * (j) statea `score: 100` si "mandatory, no exceptions", scrise de mana. Pe
   * parola e adevarat (cod pe email la fiecare intrare), dar conturile LDAP /
   * Active Directory intra FARA al doilea factor, iar cele OIDC / SAML il au la
   * furnizorul de identitate, unde portalul nu vede. Acum se numara pe conturi:
   * scorul e partea dovedita de portal; restul se arata, cu motivul.
   */
  const mfa = rec(page.mfa)
  const accounts = n(mfa.accounts)
  const mfaMeasured = Boolean(mfa.ready) && accounts > 0
  const mfaEvidence: Evidence[] = []
  if (mfaMeasured) {
    mfaEvidence.push({
      label: 'password accounts - a one-time code by email on every sign-in',
      value: `${formatNumber(n(mfa.with_code))} of ${formatNumber(accounts)}`,
    })
    if (n(mfa.ldap) > 0) {
      mfaEvidence.push({
        label: 'directory (LDAP / AD) accounts - the directory password only, no second factor',
        value: formatNumber(n(mfa.ldap)),
        bad: true,
      })
    }
    if (n(mfa.federated) > 0) {
      mfaEvidence.push({
        label: 'single sign-on accounts (OIDC / SAML) - the second factor is the identity provider\'s, not visible here',
        value: `${formatNumber(n(mfa.federated))} - to confirm with the provider`,
      })
    }
    if (mfa.active !== null && mfa.active !== undefined) {
      mfaEvidence.push({ label: `signed in during the last ${NIS2_WINDOW_DAYS} days`, value: formatNumber(n(mfa.active)) })
    }
    if (mfa.admin_sso_required === true) {
      mfaEvidence.push({
        label: 'administrators must sign in through single sign-on',
        value: n(mfa.ldap) > 0 ? 'yes - through the directory, so without a second factor' : 'yes',
        bad: n(mfa.ldap) > 0,
      })
    }
  }
  letters.push({
    key: 'j',
    name: 'Multi-factor authentication',
    asks: 'a second factor on every sign-in',
    state: mfaMeasured ? 'measured' : 'off',
    score: mfaMeasured ? percent(n(mfa.with_code), accounts) : null,
    link: null,
    link_label: null,
    evidence: mfaEvidence,
    off_reason: mfa.ready
      ? 'not known: no account that can sign in was found'
      : 'not known: the portal accounts could not be read on this instance (the sign-in source of each account is missing)',
  })

  return letters
}

export function nis2Overall(letters: Letter[]): { overall: number | null; measured: number; manual: number } {
  const scores = letters.map((l) => l.score).filter((s): s is number => s !== null)
  return {
    overall: scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null,
    measured: scores.length,
    manual: letters.filter((l) => l.state === 'manual').length,
  }
}
